'use client';

/**
 * Turnstile Widget
 * Reusable Cloudflare Turnstile CAPTCHA component for authentication forms.
 * Gracefully degrades when the site key is not configured (e.g. dev
 * environments), when the env-level kill switch
 * `NEXT_PUBLIC_TURNSTILE_ENABLED` is set to `"false"`, or when the QA /
 * pen-test bypass cookie is present (set by the middleware when a request
 * carries a matching `x-turnstile-bypass` header).
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

/** Flag cookie set by the middleware when the bypass header matches the secret. */
const BYPASS_COOKIE = 'turnstile-bypass';
function hasBypassCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some(entry => entry === `${BYPASS_COOKIE}=1`);
}
export interface TurnstileWidgetProps {
  /** Called when the Turnstile challenge is successfully completed */
  onSuccess: (token: string) => void;
  /** Called when the Turnstile challenge encounters an error */
  onError?: () => void;
  /** Called when the Turnstile token expires */
  onExpire?: () => void;
  /** Optional additional CSS class name */
  className?: string;
  /**
   * Change this value to force the widget to re-render (e.g. after a form error).
   * Incrementing a counter works well.
   */
  resetKey?: number;
  /**
   * Label for this widget, echoed by siteverify. Set it where the route
   * asserts `expectedAction`, so a token minted on another form of ours
   * cannot be spent here.
   */
  action?: string;
}
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

// Env-level kill switch. Strict equality with "false" keeps the default
// fail-closed: any other value (unset, "true", typos) leaves the widget
// enabled. Public flag because the check has to happen client-side.
const ENABLED = process.env.NEXT_PUBLIC_TURNSTILE_ENABLED !== 'false';
export default function TurnstileWidget(t0) {
  const $ = _c(20);
  const {
    onSuccess,
    onError,
    onExpire,
    className,
    resetKey,
    action
  } = t0;
  const [hydrated, setHydrated] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHydrated(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== hydrated) {
    t3 = hydrated && hasBypassCookie();
    $[2] = hydrated;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const bypassed = t3;
  let t4;
  let t5;
  if ($[4] !== bypassed || $[5] !== hydrated || $[6] !== onSuccess) {
    t4 = () => {
      if (!hydrated) {
        return;
      }
      if ((!ENABLED || !SITE_KEY || bypassed) && onSuccess) {
        onSuccess("turnstile-disabled");
      }
    };
    t5 = [hydrated, bypassed, onSuccess];
    $[4] = bypassed;
    $[5] = hydrated;
    $[6] = onSuccess;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (!ENABLED || !SITE_KEY || bypassed || !hydrated) {
    return null;
  }
  let t6;
  if ($[9] !== action) {
    t6 = {
      theme: "auto",
      size: "flexible",
      ...(action ? {
        action
      } : {})
    };
    $[9] = action;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onError || $[12] !== onExpire || $[13] !== onSuccess || $[14] !== resetKey || $[15] !== t6) {
    t7 = <Turnstile key={resetKey} siteKey={SITE_KEY} onSuccess={onSuccess} onError={onError} onExpire={onExpire} options={t6} />;
    $[11] = onError;
    $[12] = onExpire;
    $[13] = onSuccess;
    $[14] = resetKey;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== className || $[18] !== t7) {
    t8 = <div className={className}>{t7}</div>;
    $[17] = className;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}